import React, { useEffect, useState } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Spinner,
    Table
} from "reactstrap";
import { Navigate, useNavigate } from "react-router-dom";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import { sessionEnded } from "../user/SessionEnded";

const DEFAULT_FILTERS = {
    accountType: "ALL",
    enterpriseState: "ALL",
    subscriptionStatus: "ALL",
    search: ""
};

function statusColor(status) {
    const colors = {
        ACTIVE: "success",
        RESTORED: "success",
        PAST_DUE: "warning",
        RESTRICTED: "warning",
        SUSPENDED: "danger",
        active: "success",
        trialing: "info",
        past_due: "warning",
        canceled: "danger",
        unpaid: "danger"
    };
    return colors[status] || "secondary";
}

function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminOperations() {
    const navigate = useNavigate();
    const [unauthorized, setUnauthorized] = useState(false);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [counters, setCounters] = useState(null);
    const [pageData, setPageData] = useState({ content: [], number: 0, totalPages: 0 });
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (!parsed?.user?.isAdmin) setUnauthorized(true);
        } catch {
            setUnauthorized(true);
        }
    }, []);

    useEffect(() => {
        if (!unauthorized) {
            Api.get("/api/admin/operations/counters")
                .then(({ data }) => setCounters(data))
                .catch(() => setError("Unable to load operational counters."));
        }
    }, [unauthorized]);

    useEffect(() => {
        if (unauthorized) return;
        setLoading(true);
        setError("");
        Api.get("/api/admin/operations/accounts", {
            params: {
                ...filters,
                accountType: filters.accountType === "ALL" ? "" : filters.accountType,
                enterpriseState: filters.enterpriseState === "ALL" ? "" : filters.enterpriseState,
                subscriptionStatus: filters.subscriptionStatus === "ALL" ? "" : filters.subscriptionStatus,
                page,
                size: 25
            }
        })
            .then(({ data }) => setPageData(data || { content: [], number: 0, totalPages: 0 }))
            .catch(() => setError("Unable to load accounts."))
            .finally(() => setLoading(false));
    }, [filters, page, unauthorized]);

    if (!sessionStorage.getItem("customer")) return sessionEnded();
    if (unauthorized) return <Navigate to="/" />;

    const updateFilter = (key, value) => {
        setPage(0);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const totalPages = pageData.totalPages || 1;
    const currentPage = pageData.number || 0;

    return (
        <>
            <SeoNoIndex noindex={true} />

            <div className="position-relative">
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">
                                        Building your song-flows<br /><span>manage operations</span>
                                    </h1>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Row className="row-grid">
                                    <Col lg="12">
                                        <Card className="shadow border-0">
                                            <CardBody className="py-5">

                                                <Button color="link" onClick={() => navigate("/admin")} className="mb-4 p-0">
                                                    &larr; Back to menu
                                                </Button>

                                                {error && <div className="alert alert-danger mb-4">{error}</div>}

                                                {/* Operational counters */}
                                                <Row className="mb-4">
                                                    <Col md="4" lg="2" className="mb-3">
                                                        <div className="p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                                                            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>Organisation</div>
                                                            <div className="h4 mb-0" style={{ color: "#32325d" }}>{counters?.totalEnterpriseAccounts ?? "—"}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md="4" lg="2" className="mb-3">
                                                        <div className="p-3 rounded" style={{ backgroundColor: "#fffbeb" }}>
                                                            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>Past due</div>
                                                            <div className="h4 mb-0" style={{ color: "#f4a100" }}>{counters?.pastDueEnterpriseAccounts ?? "—"}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md="4" lg="2" className="mb-3">
                                                        <div className="p-3 rounded" style={{ backgroundColor: "#fef0e7" }}>
                                                            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>Restricted</div>
                                                            <div className="h4 mb-0" style={{ color: "#fb6340" }}>{counters?.restrictedEnterpriseAccounts ?? "—"}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md="4" lg="2" className="mb-3">
                                                        <div className="p-3 rounded" style={{ backgroundColor: "#fff0f0" }}>
                                                            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>Suspended</div>
                                                            <div className="h4 mb-0" style={{ color: "#f5365c" }}>{counters?.suspendedEnterpriseAccounts ?? "—"}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md="4" lg="2" className="mb-3">
                                                        <div className="p-3 rounded" style={{ backgroundColor: "#fef0e7" }}>
                                                            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>Overdue periods</div>
                                                            <div className="h4 mb-0" style={{ color: "#fb6340" }}>{counters?.overdueOpenBillingPeriods ?? "—"}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md="4" lg="2" className="mb-3">
                                                        <div className="p-3 rounded" style={{ backgroundColor: "#fff0f0" }}>
                                                            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>Open invoices</div>
                                                            <div className="h4 mb-0" style={{ color: "#f5365c" }}>{counters?.openInvoices ?? "—"}</div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Filters */}
                                                <Row className="mb-4">
                                                    <Col md="3" className="mb-2">
                                                        <Input type="select" value={filters.accountType} onChange={e => updateFilter("accountType", e.target.value)}>
                                                            <option value="ALL">All account types</option>
                                                            <option value="GROUP">Group</option>
                                                            <option value="ENTERPRISE">Organisation</option>
                                                        </Input>
                                                    </Col>
                                                    <Col md="3" className="mb-2">
                                                        <Input type="select" value={filters.enterpriseState} onChange={e => updateFilter("enterpriseState", e.target.value)}>
                                                            <option value="ALL">All lifecycle states</option>
                                                            <option value="ACTIVE">Active</option>
                                                            <option value="PAST_DUE">Past due</option>
                                                            <option value="RESTRICTED">Restricted</option>
                                                            <option value="SUSPENDED">Suspended</option>
                                                            <option value="RESTORED">Restored</option>
                                                        </Input>
                                                    </Col>
                                                    <Col md="3" className="mb-2">
                                                        <Input type="select" value={filters.subscriptionStatus} onChange={e => updateFilter("subscriptionStatus", e.target.value)}>
                                                            <option value="ALL">All subscription statuses</option>
                                                            <option value="active">active</option>
                                                            <option value="trialing">trialing</option>
                                                            <option value="past_due">past_due</option>
                                                            <option value="canceled">canceled</option>
                                                        </Input>
                                                    </Col>
                                                    <Col md="3" className="mb-2">
                                                        <Input
                                                            value={filters.search}
                                                            placeholder="Search name or owner email"
                                                            onChange={e => updateFilter("search", e.target.value)}
                                                        />
                                                    </Col>
                                                </Row>

                                                {/* Accounts table */}
                                                {loading ? (
                                                    <div className="text-center py-5"><Spinner color="primary" /></div>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <Table className="align-items-center" hover>
                                                            <thead className="thead-light">
                                                            <tr>
                                                                <th>Name</th>
                                                                <th>Type</th>
                                                                <th>Lifecycle</th>
                                                                <th>Subscription</th>
                                                                <th>Seats</th>
                                                                <th>Owner</th>
                                                                <th>Created</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {(pageData.content || []).length === 0 ? (
                                                                <tr>
                                                                    <td colSpan={7} className="text-muted text-center py-4">No accounts found</td>
                                                                </tr>
                                                            ) : (pageData.content || []).map(account => (
                                                                <tr
                                                                    key={account.id}
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() => navigate(`/admin/operations/account/${account.id}`)}
                                                                >
                                                                    <td className="font-weight-bold">{account.name}</td>
                                                                    <td>{account.accountType}</td>
                                                                    <td>
                                                                        {account.enterpriseState
                                                                            ? <Badge color={statusColor(account.enterpriseState)}>{account.enterpriseState}</Badge>
                                                                            : <span className="text-muted">—</span>}
                                                                    </td>
                                                                    <td>
                                                                        {account.subscriptionStatus
                                                                            ? <Badge color={statusColor(account.subscriptionStatus)}>{account.subscriptionStatus}</Badge>
                                                                            : <span className="text-muted">—</span>}
                                                                    </td>
                                                                    <td>{account.activeSeats}{account.maxSeats ? ` / ${account.maxSeats}` : ""}</td>
                                                                    <td>{account.ownerName || account.ownerEmail || "—"}</td>
                                                                    <td>{formatDate(account.createdAt)}</td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <Pagination aria-label="Accounts pagination" className="justify-content-center mt-4 flex-wrap">
                                                        <PaginationItem disabled={currentPage === 0}>
                                                            <PaginationLink previous onClick={() => setPage(prev => Math.max(0, prev - 1))} />
                                                        </PaginationItem>
                                                        {Array.from({ length: totalPages }, (_, i) => (
                                                            <PaginationItem active={i === currentPage} key={i}>
                                                                <PaginationLink onClick={() => setPage(i)}>{i + 1}</PaginationLink>
                                                            </PaginationItem>
                                                        ))}
                                                        <PaginationItem disabled={currentPage >= totalPages - 1}>
                                                            <PaginationLink next onClick={() => setPage(prev => prev + 1)} />
                                                        </PaginationItem>
                                                    </Pagination>
                                                )}
                                                {totalPages <= 1 && (
                                                    <p className="text-muted text-center mt-3 small">
                                                        {(pageData.content || []).length} account{(pageData.content || []).length !== 1 ? "s" : ""}
                                                    </p>
                                                )}

                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
}
